const isLocalHost =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const configuredApiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").trim().replace(/\/+$/, "");
const apiBaseUrl = configuredApiBaseUrl || (isLocalHost ? "http://localhost:8000" : "");
const discoveryRelayUrl = (process.env.REACT_APP_DISCOVERY_EMAIL_RELAY_URL || "").trim();
const discoveryRelayToken = (process.env.REACT_APP_DISCOVERY_EMAIL_RELAY_TOKEN || "").trim();

export const isDiscoveryApiConfigured = Boolean(apiBaseUrl);
export const getDiscoveryApiBaseUrl = () => apiBaseUrl;
export const isDiscoveryRelayConfigured = Boolean(discoveryRelayUrl);
export const getDiscoveryRelayUrl = () => discoveryRelayUrl;

const buildEndpoint = (path) => `${apiBaseUrl}${path}`;

const parseResponse = async (response) => {
  let raw = "";
  let payload = null;
  try {
    raw = await response.text();
    payload = raw ? JSON.parse(raw) : null;
  } catch (_error) {
    payload = null;
  }

  if (!response.ok) {
    const detail = payload?.detail || payload?.message || payload?.error;
    if (typeof detail === "string" && detail.trim()) {
      throw new Error(detail);
    }

    const httpStatus = `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ""}`;
    throw new Error(`Request failed (${httpStatus})`);
  }

  return payload;
};

export const startWizardSession = async (payload) => {
  const response = await fetch(buildEndpoint("/api/wizard/session/start"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  return parseResponse(response);
};

export const updateWizardSession = async (sessionId, payload) => {
  const response = await fetch(buildEndpoint(`/api/wizard/session/${sessionId}`), {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  return parseResponse(response);
};

export const submitWizardLead = async (payload) => {
  const response = await fetch(buildEndpoint("/api/wizard/lead/submit"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  return parseResponse(response);
};

export const submitWizardLeadRelay = async (payload) => {
  if (!discoveryRelayUrl) {
    throw new Error("Discovery email relay endpoint is not configured.");
  }

  const headers = { "Content-Type": "application/json" };
  if (discoveryRelayToken) {
    headers["x-discovery-relay-token"] = discoveryRelayToken;
  }

  const response = await fetch(discoveryRelayUrl, {
    method: "POST",
    headers,
    body: JSON.stringify(payload),
  });

  return parseResponse(response);
};

export const requestMagicLink = async (email) => {
  const response = await fetch(buildEndpoint("/api/auth/magic-link/request"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });

  return parseResponse(response);
};

export const verifyMagicLink = async (token) => {
  const response = await fetch(buildEndpoint("/api/auth/magic-link/verify"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });

  return parseResponse(response);
};

export const authenticateWithProvider = async (provider, { idToken, email }) => {
  const endpoint = provider === "apple" ? "/api/auth/apple" : "/api/auth/google";

  const response = await fetch(buildEndpoint(endpoint), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken, email }),
  });

  return parseResponse(response);
};

export const getAvailabilitySlots = async (timezone) => {
  const params = new URLSearchParams();
  if (timezone) {
    params.set("timezone", timezone);
  }

  const response = await fetch(
    buildEndpoint(`/api/availability/slots${params.toString() ? `?${params.toString()}` : ""}`),
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );

  return parseResponse(response);
};

export const createAppointment = async (payload) => {
  const response = await fetch(buildEndpoint("/api/appointments"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  return parseResponse(response);
};

export const trackSiteVisit = async (payload) => {
  const response = await fetch(buildEndpoint("/api/analytics/visit"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  return parseResponse(response);
};
